import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Tooltip } from 'antd';
import {
  isApprovalAsset,
  useAssetsSliderNavigation,
  useAssetsSliderReview,
  useScrollToActiveAsset
} from './hooks';
import { useResponsive } from '@hooks/useResponsive';
import { ApprovalWorkflowItemDto, AssetItemDto, AWAssetDto } from '@api/Api';
import { useTypedSelector } from '@hooks';
import { useMediaViewerMobileActionVisibility } from '@pages/MediaViewer/components/MediaViewerMobileAction/MediaViewerMobileAction';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { setAssetsSliderOpen } from '@redux/actions/mediaViewerAction';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { ArrowDownIcon, CheckTaskIcon, LockIcon } from '@icons';

import './AssetsSlider.less';

type AssetsSliderItemProps = {
  asset: AWAssetDto | AssetItemDto;
  isActive: boolean;
  approvalWorkflow?: ApprovalWorkflowItemDto;
  onClick: () => void;
};

function AssetsSliderItem({
  asset,
  isActive,
  approvalWorkflow,
  onClick
}: AssetsSliderItemProps) {
  const lockDecisions = !!approvalWorkflow?.lockDecisions;
  const decision = isApprovalAsset(asset) ? asset.decision : null;
  const accessDisabled = isApprovalAsset(asset) ? asset.accessDisabled : false;

  const disableReason =
    lockDecisions && !!decision ? (
      <>
        Your decision is locked.
        <br />
        Contact the approval manager
        <br />
        if you need to change it.
      </>
    ) : accessDisabled ? (
      <>
        Your access to this media is restricted.
        <br />
        Contact the approval manager
        <br />
        if you need to request access.
      </>
    ) : null;

  const showLock = !!disableReason;
  const grayOut = !!disableReason || !!decision;

  const previewUrl = isApprovalAsset(asset)
    ? asset.previewUrl
    : asset.asset?.versions[0]?.metadata.preview?.url;
  const assetType = isApprovalAsset(asset)
    ? asset.type
    : asset.asset?.versions[0]?.metadata.type;

  const preparePreview = useMemo(() => {
    const res = assetPreviewThumbnails({
      url: previewUrl,
      status: 'ready',
      type: assetType || 'unknown',
      format: 'img'
    });

    if (typeof res === 'string')
      return <img draggable={false} src={res || ''} alt="" />;
    return res;
  }, [previewUrl, assetType]);

  const name = isApprovalAsset(asset)
    ? asset.name
    : asset.asset?.versions[0]?.name;
  const extension = isApprovalAsset(asset)
    ? asset.extension
    : asset.asset?.versions[0]?.extension;
  const versionNumber = isApprovalAsset(asset)
    ? asset.versionsCount
    : asset.asset?.versions[0]?.versionNumber;

  return (
    <Tooltip
      overlayClassName="assets-slider_tooltip"
      title={
        <>
          {!disableReason && (
            <div className="assets-slider_tooltip-head">
              <div className="assets-slider_tooltip-name">
                {name}
                <span className="assets-slider_tooltip-extension">
                  .{extension}
                </span>
              </div>
              <div className="assets-slider_tooltip-version">
                V{versionNumber}
              </div>
            </div>
          )}
          {disableReason && (
            <div className="assets-slider_tooltip-body">{disableReason}</div>
          )}
        </>
      }
    >
      <div
        className={classNames('assets-slider_item', {
          'assets-slider_item--active': isActive,
          'assets-slider_item--disabled': grayOut,
          'assets-slider_item--blur': accessDisabled
        })}
        onClick={accessDisabled ? undefined : onClick}
        style={{ cursor: accessDisabled ? 'not-allowed' : undefined }}
      >
        {showLock && (
          <div className="assets-slider_item-lock-icon">
            <LockIcon />
          </div>
        )}
        {preparePreview}
        {!!decision && (
          <div
            className={classNames([
              'assets-slider_item-icon',
              `b-color-${decision}`
            ])}
          >
            <CheckTaskIcon />
          </div>
        )}
      </div>
    </Tooltip>
  );
}

function AssetsSliderMobile() {
  const { tourData, goToStep } = useOnboardingProcess();
  const selectedAsset = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.assets
  );
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const { aw, sortedAssets, count, selectedAssetId, onChangeAsset } =
    useAssetsSliderNavigation();
  const { reviewPositionLabel, isReviewed } = useAssetsSliderReview(aw);
  const canShowFloatComments = !!selectedAsset && !!selectedVersion;
  const mobileVisibility = useMediaViewerMobileActionVisibility({
    isShowFloatCommentForm: canShowFloatComments,
    isShowFloatCommentItem: canShowFloatComments
  });
  const isAnnouncementMediaViewerMobileTour =
    tourData?.type === 'announcement-media-viewer-mobile';
  const isAnnouncementMediaViewerCarouselTour =
    isAnnouncementMediaViewerMobileTour && goToStep === 0;
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const hideMobileReviewBanner = !!mobileVisibility?.showFloatCommentItem;

  useEffect(() => {
    if (hideMobileReviewBanner) {
      setIsMobileExpanded(false);
    }
  }, [hideMobileReviewBanner]);

  useScrollToActiveAsset({
    contentRef,
    sortedAssets,
    selectedAssetId,
    enabled: isMobileExpanded
  });

  if (count <= 1 && !isAnnouncementMediaViewerMobileTour) {
    return null;
  }

  if (hideMobileReviewBanner) {
    return null;
  }

  const sliderItems = sortedAssets.map((item) => {
    const isActive = selectedAssetId === item.id;
    return (
      <AssetsSliderItem
        asset={item}
        approvalWorkflow={aw}
        isActive={isActive}
        onClick={() => {
          if (isActive) return;
          onChangeAsset(item);
        }}
        key={item.id}
      />
    );
  });

  return (
    <div
      className={classNames('assets-slider', {
        'assets-slider--mobile-expanded': isMobileExpanded,
        'assets-slider--mobile-collapsed': !isMobileExpanded
      })}
    >
      <div
        className={classNames('assets-slider_mobile-expandable', {
          'assets-slider_mobile-expandable--expanded': isMobileExpanded
        })}
      >
        <div className="assets-slider_mobile-expandable_section">
          <div
            className={classNames(
              'assets-slider_mobile',
              'tour-announcement-media-viewer-carousel',
              {
                'tour-announcement-media-viewer-carousel-active':
                  isAnnouncementMediaViewerCarouselTour
              }
            )}
            onClick={() => setIsMobileExpanded(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsMobileExpanded(true);
              }
            }}
          >
            <div
              className={classNames('assets-slider_mobile-icon', {
                'assets-slider_mobile-icon--reviewed': isReviewed
              })}
            >
              <CheckTaskIcon />
            </div>
            <div className="assets-slider_mobile-label">
              Review{reviewPositionLabel ? `: ${reviewPositionLabel}` : ''}
            </div>
            <ArrowDownIcon className="assets-slider_mobile-chevron" />
          </div>
        </div>
        <div className="assets-slider_mobile-expandable_section">
          <div className="assets-slider_mobile-panel">
            <div ref={contentRef} className="assets-slider_content">
              {sliderItems}
            </div>
            <div
              className="assets-slider_toggle"
              role="button"
              tabIndex={0}
              onClick={() => setIsMobileExpanded(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsMobileExpanded(false);
                }
              }}
            >
              <ArrowDownIcon className="assets-slider_toggle-chevron" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssetsSliderDesktop() {
  const dispatch = useTypedDispatch();
  const isAssetsSliderOpen = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.isAssetsSliderOpen
  );
  const { aw, sortedAssets, count, selectedAssetId, onChangeAsset } =
    useAssetsSliderNavigation();
  const { reviewPositionLabel, isReviewed } = useAssetsSliderReview(aw);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentWrapRef = useRef<HTMLDivElement>(null);

  useScrollToActiveAsset({
    contentRef,
    sortedAssets,
    selectedAssetId,
    enabled: isAssetsSliderOpen
  });

  useEffect(() => {
    if (!isAssetsSliderOpen) return;
    const content = contentRef.current;
    const wrap = contentWrapRef.current;
    if (!content || !wrap) return;

    const updateFades = () => {
      const maxScroll = content.scrollWidth - content.clientWidth;
      wrap.classList.toggle(
        'assets-slider_content-wrap--fade-left',
        content.scrollLeft > 1
      );
      wrap.classList.toggle(
        'assets-slider_content-wrap--fade-right',
        content.scrollLeft < maxScroll - 1
      );
    };

    updateFades();
    content.addEventListener('scroll', updateFades);
    return () => content.removeEventListener('scroll', updateFades);
  }, [isAssetsSliderOpen, count]);

  useEffect(() => {
    if (!isAssetsSliderOpen) return;
    const content = contentRef.current;
    if (!content) return;

    let isPointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      isPointerDown = true;
      isDragging = false;
      startX = e.clientX;
      startScrollLeft = content.scrollLeft;
      content.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isPointerDown) return;
      const dx = e.clientX - startX;
      if (!isDragging && Math.abs(dx) < 5) return;
      if (!isDragging) {
        isDragging = true;
        content.classList.add('assets-slider_content--dragging');
      }
      content.scrollLeft = startScrollLeft - dx;
    };

    const stopDragging = (e: PointerEvent) => {
      if (!isPointerDown) return;
      isPointerDown = false;
      content.classList.remove('assets-slider_content--dragging');
      try {
        content.releasePointerCapture(e.pointerId);
      } catch {
        // already released
      }
      if (!isDragging) return;
      isDragging = false;
      const suppressClick = (clickEvent: MouseEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        content.removeEventListener('click', suppressClick, true);
      };
      content.addEventListener('click', suppressClick, true);
    };

    content.addEventListener('pointerdown', onPointerDown);
    content.addEventListener('pointermove', onPointerMove);
    content.addEventListener('pointerup', stopDragging);
    content.addEventListener('pointercancel', stopDragging);

    return () => {
      content.removeEventListener('pointerdown', onPointerDown);
      content.removeEventListener('pointermove', onPointerMove);
      content.removeEventListener('pointerup', stopDragging);
      content.removeEventListener('pointercancel', stopDragging);
      content.classList.remove('assets-slider_content--dragging');
    };
  }, [isAssetsSliderOpen, count]);

  if (count <= 1 || !isAssetsSliderOpen) {
    return null;
  }

  const sliderItems = sortedAssets.map((item) => {
    const isActive = selectedAssetId === item.id;
    return (
      <AssetsSliderItem
        asset={item}
        approvalWorkflow={aw}
        isActive={isActive}
        onClick={() => {
          if (isActive) return;
          onChangeAsset(item);
        }}
        key={item.id}
      />
    );
  });

  return (
    <div className="assets-slider assets-slider--desktop">
      <div className="assets-slider_desktop-panel">
        <div className="assets-slider_review-wrapper">
          <div className="assets-slider_review">
            <div
              className={classNames('assets-slider_review-icon', {
                'assets-slider_review-icon--reviewed': isReviewed
              })}
            >
              <CheckTaskIcon />
            </div>
            <div className="assets-slider_review-label">
              Review{reviewPositionLabel ? `: ${reviewPositionLabel}` : ''}
            </div>
          </div>
        </div>
        <div ref={contentWrapRef} className="assets-slider_content-wrap">
          <div ref={contentRef} className="assets-slider_content">
            {sliderItems}
          </div>
        </div>
        <div
          className="assets-slider_toggle"
          role="button"
          tabIndex={0}
          onClick={() => dispatch(setAssetsSliderOpen(false))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              dispatch(setAssetsSliderOpen(false));
            }
          }}
        >
          <ArrowDownIcon className="assets-slider_toggle-chevron" />
        </div>
      </div>
    </div>
  );
}

export default function AssetsSlider() {
  const { isMdWidth } = useResponsive();

  if (isMdWidth) {
    return <AssetsSliderMobile />;
  }

  return <AssetsSliderDesktop />;
}
